/**
 * DualCurrencyPicker — institutional dual-currency controls for Invoice Create.
 *
 * Emits the parent a full FX descriptor: { display_currency, fx_rate,
 * fx_rate_source, fx_rate_date }. These are persisted on the invoice so
 * the rate is frozen at issuance (regardless of later market moves).
 *
 * Supported currencies: AED, USD, EUR (institutional scope).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRightLeft, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Input } from '../../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['AED', 'USD', 'EUR'];

export const DualCurrencyPicker = ({
  primaryCurrency,
  displayCurrency,
  fxRate,
  fxRateSource,
  fxRateDate,
  onChange,
  previewSubtotal = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState(false);

  const fetchRate = async (manualRate = null) => {
    if (!displayCurrency || displayCurrency === primaryCurrency) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        from: primaryCurrency, to: displayCurrency,
      });
      if (manualRate) params.append('manual', manualRate);
      const r = await axios.get(`${API_URL}/api/fx/rate?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      onChange({
        display_currency: displayCurrency,
        fx_rate: r.data.rate,
        fx_rate_source: r.data.source,
        fx_rate_date: r.data.date,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to resolve FX rate');
    } finally { setLoading(false); }
  };

  // Auto-fetch on toggle / currency change
  useEffect(() => {
    if (displayCurrency && displayCurrency !== primaryCurrency && !manual) {
      fetchRate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCurrency, primaryCurrency]);

  const toggleOn = (c) => {
    if (c === primaryCurrency || c === displayCurrency) {
      onChange({ display_currency: null, fx_rate: null,
                 fx_rate_source: null, fx_rate_date: null });
    } else {
      onChange({ display_currency: c, fx_rate: null,
                 fx_rate_source: null, fx_rate_date: null });
    }
  };

  const equivalent = fxRate && previewSubtotal
    ? (previewSubtotal * fxRate).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    : null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"
      data-testid="dual-currency-picker">
      <div className="flex items-center gap-2 mb-2">
        <ArrowRightLeft className="w-4 h-4 text-[#0EA5E9]" />
        <span className="text-sm font-semibold text-[#0F172A]">
          Secondary currency (optional display)
        </span>
      </div>

      {/* Currency chips */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 self-center">
          Show equivalent in:
        </span>
        {CURRENCIES.map((c) => {
          const isPrimary = c === primaryCurrency;
          const isSelected = c === displayCurrency;
          return (
            <button key={c} type="button"
              disabled={isPrimary}
              onClick={() => toggleOn(c)}
              data-testid={`dual-cur-chip-${c}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                isPrimary
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : isSelected
                  ? 'bg-[#0EA5E9] text-white shadow'
                  : 'bg-white border border-slate-300 text-slate-600 hover:border-[#0EA5E9]'
              }`}>
              {c}{isPrimary && ' (primary)'}
            </button>
          );
        })}
      </div>

      {/* Rate details */}
      {displayCurrency && displayCurrency !== primaryCurrency && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">1 {primaryCurrency} =</span>
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : manual ? (
              <Input
                type="number" step="0.0001" min="0"
                data-testid="dual-cur-manual-rate"
                defaultValue={fxRate || ''}
                onBlur={(e) => fetchRate(parseFloat(e.target.value))}
                className="w-32 h-7 text-xs"
              />
            ) : (
              <span className="font-bold text-[#0F172A] tabular-nums">
                {fxRate ? fxRate.toFixed(6) : '—'}
              </span>
            )}
            <span className="text-slate-500">{displayCurrency}</span>
            {fxRateSource && !loading && (
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                fxRateSource === 'manual'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-700'
              }`}>
                {fxRateSource === 'manual' ? <AlertTriangle className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                {fxRateSource === 'manual' ? 'manual' : 'live'}
                {fxRateDate && ` · ${fxRateDate}`}
              </span>
            )}
            <button type="button"
              onClick={() => { setManual((v) => !v); if (!manual) setError(null); }}
              data-testid="dual-cur-toggle-manual"
              className="ml-auto text-[10px] text-[#0EA5E9] hover:underline">
              {manual ? 'Use live rate' : 'Override manually'}
            </button>
          </div>

          {equivalent !== null && (
            <div className="pt-2 border-t border-slate-200 text-xs">
              <span className="text-slate-500">Subtotal preview: </span>
              <span className="font-semibold">
                {primaryCurrency} {previewSubtotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2,
                })}
              </span>
              <span className="text-slate-400"> ≈ </span>
              <span className="font-semibold text-[#0EA5E9]"
                data-testid="dual-cur-equivalent">
                {displayCurrency} {equivalent}
              </span>
            </div>
          )}

          {error && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200
              rounded px-2 py-1 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5" />
              <span>{error}. Toggle "Override manually" to enter the rate.</span>
            </div>
          )}
          <div className="text-[10px] text-slate-500 leading-relaxed">
            This invoice is legally issued in <b>{primaryCurrency}</b>. The{' '}
            <b>{displayCurrency}</b> equivalent is shown for reference only.
            The rate is frozen at issuance and stored on the invoice.
          </div>
        </div>
      )}
    </div>
  );
};

export default DualCurrencyPicker;
